/** Phase 5 part 3 on the web: DM voice provider (xAI or ElevenLabs), NPC voice casting mode, character import. */
import { useEffect, useRef, useState } from "react";
import { XAI_VOICES, type Campaign, type CharacterImport, type CharacterSheet, type ElevenVoice, type NpcVoiceMode, type TtsProvider } from "@dm/shared";
import { api } from "./api.js";

const describe = (v: ElevenVoice) => [v.labels.gender, v.labels.age, v.labels.accent, v.labels.description ?? v.labels.descriptive, v.labels.use_case ?? v.labels.use_case]
  .filter(Boolean).join(", ");

/** The Host's ElevenLabs voices (Host/Co-host only; an error message when there is no key). */
export function useElevenVoices(campaignId: number, enabled: boolean): { voices: ElevenVoice[]; error: string | null; reload: () => void } {
  const [voices, setVoices] = useState<ElevenVoice[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    api.elevenVoices(campaignId, n > 0).then((r) => { setVoices(r.voices); setError(null); }, (e: Error) => setError(e.message));
  }, [campaignId, enabled, n]);
  return { voices, error, reload: () => setN((x) => x + 1) };
}

/** Campaign settings: who voices the DM, and how NPCs get voices. Host only. */
export function VoiceSettings({ campaign, owner, onSaved }: { campaign: Campaign; owner: boolean; onSaved: () => void }) {
  const [provider, setProvider] = useState<TtsProvider>(campaign.ttsProvider);
  const [voiceId, setVoiceId] = useState(campaign.elevenVoiceId ?? "");
  const [mode, setMode] = useState<NpcVoiceMode>(campaign.npcVoiceMode);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const el = useElevenVoices(campaign.id, owner && provider === "elevenlabs");
  const audio = useRef<HTMLAudioElement | null>(null);
  const picked = el.voices.find((v) => v.id === voiceId);
  const save = async () => {
    setBusy(true);
    try {
      await api.setVoiceSettings(campaign.id, { ttsProvider: provider, elevenVoiceId: provider === "elevenlabs" ? voiceId || null : campaign.elevenVoiceId, npcVoiceMode: mode });
      setMsg("Saved. The DM uses it from the next thing it says.");
      onSaved();
    } catch (err) {
      setMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="panel">
      <h3>Voices</h3>
      <div className="voice-grid">
        <label className="field">
          <span>DM voice provider</span>
          <select value={provider} disabled={!owner} onChange={(e) => setProvider(e.target.value as TtsProvider)}>
            <option value="xai">xAI voices (on the campaign's xAI key)</option>
            <option value="elevenlabs">ElevenLabs (on your own ElevenLabs key)</option>
          </select>
        </label>
        {provider === "elevenlabs" && owner && (
          <label className="field">
            <span>ElevenLabs voice for the DM</span>
            {el.error ? (
              <span className="warn small">{el.error} <a href="#/settings/xai">Settings → API keys</a></span>
            ) : (
              <span className="row">
                <select value={voiceId} onChange={(e) => setVoiceId(e.target.value)}>
                  <option value="">{el.voices.length ? "Pick a voice…" : "Loading your voices…"}</option>
                  {el.voices.map((v) => (
                    <option key={v.id} value={v.id}>{v.name}{describe(v) ? ` (${describe(v)})` : ""}</option>
                  ))}
                </select>
                <button type="button" disabled={!picked?.previewUrl} onClick={() => { if (picked?.previewUrl) { audio.current?.pause(); audio.current = new Audio(picked.previewUrl); void audio.current.play(); } }}>▶ Preview</button>
                <button type="button" title="Reload your ElevenLabs voices" onClick={el.reload}>↻</button>
              </span>
            )}
          </label>
        )}
        <label className="field">
          <span>NPC voices</span>
          <select value={mode} disabled={!owner} onChange={(e) => setMode(e.target.value as NpcVoiceMode)}>
            <option value="auto">Automatic: every named NPC gets their own voice</option>
            <option value="manual">Manual: only voices set on the Memory page</option>
            <option value="off">Off: the DM's voice for everyone</option>
          </select>
        </label>
      </div>
      <p className="muted small">
        {provider === "elevenlabs"
          ? "ElevenLabs speech is billed to your ElevenLabs account (shown as unpriced in Costs unless the Admin adds a price). If your ElevenLabs key fails, the DM pauses and tells you, like with xAI. Automatic NPC voices come from your ElevenLabs voice library, matched to each NPC's description."
          : `Automatic NPC voices pick from xAI's ${XAI_VOICES.length} voices, matched to each NPC's description and kept distinct.`}
      </p>
      {owner && (
        <div className="quick-row">
          <button className="primary" disabled={busy || (provider === "elevenlabs" && !voiceId)} onClick={save}>Save voices</button>
          {msg && <span className="small">{msg}</span>}
        </div>
      )}
    </section>
  );
}

// ------------------------------------------------------------------ character import

const readFile = (f: File) =>
  new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error("Couldn't read the file"));
    r.readAsDataURL(f);
  });

/** "Import sheet": PDF (fillable) or a photo/screenshot -> review -> fill the sheet. */
export function ImportSheet({ campaignId, isNew, onUse, fail }: {
  campaignId: number; isNew: boolean; onUse: (s: CharacterSheet, avatar: string | null) => Promise<void> | void; fail: (e: unknown) => void;
}) {
  const input = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<CharacterImport | null>(null);
  const pick = async (f: File | undefined) => {
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) return fail(new Error("That file is over 10 MB."));
    setBusy(true);
    try {
      const data = await readFile(f);
      setResult(await api.importCharacter(campaignId, { fileName: f.name, mime: f.type || "application/octet-stream", data }));
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };
  const s = result?.sheet;
  return (
    <>
      <input ref={input} type="file" accept=".json,application/json,application/pdf,image/png,image/jpeg,image/webp" hidden onChange={(e) => void pick(e.target.files?.[0])} />
      <button type="button" disabled={busy} onClick={() => input.current?.click()} title="Fill this sheet from a character exported from this app (.json or .pdf), any PDF character sheet, or a photo/screenshot of one">
        {busy ? "Reading…" : "📄 Import sheet"}
      </button>
      {result && s && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Imported character">
          <div className="modal import-review">
            <h3>Imported: {s.name || "(no name)"}</h3>
            <p className="muted small">
              {result.method === "exact" ? "From your exported character: an exact copy" : result.source === "pdf" ? "From the PDF's form fields" : "Read from the photo by Grok"}
              {result.method === "fields+ai" ? " (Grok checked the spell levels)" : result.method === "ai" && result.source === "pdf" ? " (Grok mapped the fields)" : result.method === "text" ? " (Grok read the PDF text)" : result.method === "scan" ? " (Grok read the scanned pages)" : ""}.
            </p>
            {result.warnings.length > 0 && <ul className="warn small">{result.warnings.map((w) => <li key={w}>{w}</li>)}</ul>}
            <table className="simple small">
              <tbody>
                <tr><th>Race / class</th><td>{[s.race, s.className].filter(Boolean).join(" ")} {s.level}{s.details.multiclass.map((m) => ` / ${m.className} ${m.level}`).join("")}{s.details.subclass ? ` (${s.details.subclass})` : ""}</td></tr>
                <tr><th>Abilities</th><td>{Object.entries(s.abilities).map(([k, v]) => `${k.toUpperCase()} ${v}`).join(" · ")}</td></tr>
                <tr><th>HP / AC / Speed</th><td>{s.currentHp}/{s.maxHp} · AC {s.ac} · {s.speed} ft</td></tr>
                <tr><th>Saves</th><td>{s.saveProficiencies.map((x) => x.toUpperCase()).join(", ") || "—"}</td></tr>
                <tr><th>Skills</th><td>{s.skillProficiencies.map((x) => x.replace(/_/g, " ")).join(", ") || "—"}</td></tr>
                <tr><th>Attacks</th><td>{s.details.attacks.map((a) => a.name).join(", ") || "—"}</td></tr>
                <tr><th>Spells</th><td>{s.details.spellcasting.spells.length ? s.details.spellcasting.spells.map((x) => `${x.name}${x.level ? ` (${x.level})` : ""}`).join(", ") : "—"}</td></tr>
                <tr><th>Equipment</th><td>{s.details.equipment.map((e) => (e.qty > 1 ? `${e.qty} ${e.name}` : e.name)).join(", ") || "—"}</td></tr>
              </tbody>
            </table>
            <p className="muted small">
              {isNew ? "This fills in the new character form: check it, then press Create character." : "This replaces everything on your current sheet (HP, spells, equipment...)."}
            </p>
            <div className="row-end">
              <button onClick={() => setResult(null)}>Cancel</button>
              <button
                className="primary"
                onClick={async () => {
                  if (!isNew && !window.confirm("Replace your whole sheet with the imported one?")) return;
                  await onUse(s, result.avatar ?? null);
                  setResult(null);
                }}
              >
                {isNew ? "Use this" : "Replace my sheet"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
